import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function HeadlightRestorationPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Headlight Restoration',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks'],
    description: 'Professional headlight restoration service to remove yellowing, oxidation, and fog, improving nighttime visibility and vehicle appearance.'
  };
  const faqs = [{
    question: 'How long does headlight restoration last?',
    answer: 'Because we apply a durable UV sealant as the final step, our headlight restoration lasts far longer than cheap DIY kits — typically a year or more, depending on sun exposure and care.'
  }, {
    question: 'Why do headlights turn yellow and cloudy?',
    answer: 'Headlight lenses are made of polycarbonate plastic with a factory UV clear coat. Over time that coating breaks down from sun and heat, causing the plastic underneath to oxidize and turn yellow or foggy.'
  }, {
    question: 'Is headlight restoration worth it vs. replacement?',
    answer: 'Yes. Replacing headlight assemblies can cost hundreds or thousands of dollars. Restoration delivers clear, like-new lenses for a fraction of the cost.'
  }, {
    question: 'Does it improve nighttime visibility?',
    answer: 'Significantly. Clear lenses let your bulbs project light properly, improving your vision at night and in poor weather and keeping you safer on the road.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Headlight Restoration',
    item: 'https://www.fulldetailpro.com/services/headlight-restoration'
  }];
  return <ServicePageLayout title="Headlight Restoration Ventura County" subtitle="Improve your nighttime visibility and enhance your car's appearance by permanently removing yellowing, oxidation, and fog from your headlights." metaDescription="Professional headlight restoration in Ventura County \u2014 Camarillo, Oxnard & Thousand Oaks. Remove yellowing & oxidation for clear headlights. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-924" data-mp="21ctb05174qx">
      <p data-id="element-925" data-mp="3kekekhbxbgkg">
        Are your vehicle's headlights looking cloudy, yellowed, or foggy? Not
        only does this age the appearance of your car significantly, but it also
        poses a serious safety hazard by drastically reducing your nighttime
        visibility. At Mike's Pro Detail, our professional{' '}
        <strong data-id="element-926" data-mp="2wp87bn05piks">Headlight Restoration</strong> service permanently removes heavy
        oxidation and restores crystal-clear clarity to your lenses. We provide
        this essential mobile service throughout Camarillo CA, Thousand Oaks,
        and the greater Ventura County area.
      </p>

      <h2 data-id="element-927" data-mp="1erd8sbfuarkr">Why Do Headlights Turn Yellow?</h2>
      <p data-id="element-928" data-mp="1esoeigsui9ei">
        Modern vehicle headlights are made of polycarbonate plastic, which is
        lightweight and impact-resistant. However, this plastic is highly
        susceptible to UV degradation from the sun. Manufacturers apply a
        UV-protective clear coat at the factory, but over time, this coating
        breaks down due to sun exposure, heat from the bulbs, and harsh cleaning
        chemicals. Once the coating fails, the plastic oxidizes rapidly,
        resulting in that cloudy, yellow appearance.
      </p>

      <h2 data-id="element-929" data-mp="qzodqnbmvp04">Our Professional Restoration Process</h2>
      <p data-id="element-930" data-mp="m3h68q3la5g2">
        Don't be fooled by cheap DIY kits or "wipe-on" fixes that only last a
        few weeks. We perform a true, multi-step mechanical restoration to
        permanently fix the issue:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-931" data-mp="c5jie3pn6anz">
        <li className="flex items-start" data-id="element-932" data-mp="2c6rfdhc03gmk">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-933" data-mp="2c7ssrsghof7g" />
          <span data-id="element-934" data-mp="2h5ks37jgr3lo">
            <strong data-id="element-935" data-mp="2h64304ap4rq7">Taping & Protection:</strong> We carefully tape off the
            surrounding paint and trim to ensure no damage occurs during the
            sanding process.
          </span>
        </li>
        <li className="flex items-start" data-id="element-936" data-mp="27ezp77wrbcwy">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-937" data-mp="27dok94rkrz49" />
          <span data-id="element-938" data-mp="1teowdzbvzo95">
            <strong data-id="element-939" data-mp="1ticpixdf68nc">Wet Sanding:</strong> We use progressive grits of
            specialized sandpaper to completely strip away the failed factory
            clear coat and the oxidized plastic layer.
          </span>
        </li>
        <li className="flex items-start" data-id="element-940" data-mp="1ogx4347by04f">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-941" data-mp="1ohyhhfbtiypb" />
          <span data-id="element-942" data-mp="3dvldqxojd3yr">
            <strong data-id="element-943" data-mp="3dx68anauomwm">Machine Compounding:</strong> Using a machine polisher and
            heavy cutting compound, we remove the fine sanding marks and begin
            restoring clarity.
          </span>
        </li>
        <li className="flex items-start" data-id="element-944" data-mp="178e0npkf68om">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-945" data-mp="128rmndwrooer" />
          <span data-id="element-946" data-mp="126opm9x75v0j">
            <strong data-id="element-947" data-mp="1gxnupaytq1fd">Machine Polishing:</strong> A refining polish is used to
            bring the polycarbonate lens back to a flawless, glass-like
            transparency.
          </span>
        </li>
        <li className="flex items-start" data-id="element-948" data-mp="1pyc9w8zye7tu">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-949" data-mp="1pxazm6qrhpd6" />
          <span data-id="element-950" data-mp="1lx0pchvxscsi">
            <strong data-id="element-951" data-mp="1lxiopndwb74n">UV Protection Application:</strong> This is the most
            critical step. We apply a durable specialized UV
            sealant to protect the bare plastic and prevent it from yellowing
            again.
          </span>
        </li>
      </ul>

      <h2 data-id="element-952" data-mp="5ih9fvr048tv">Safety and Aesthetics</h2>
      <p data-id="element-953" data-mp="5jk0c3obqb9s">
        Restoring your headlights is one of the most cost-effective ways to
        instantly improve the look of your vehicle. More importantly, clear
        headlights allow your bulbs to project light properly, significantly
        improving your vision at night and during inclement weather, keeping you
        and your family safe on the road.
      </p>

      <h2 data-id="element-954" data-mp="29mlpt2aq5exg">Convenient Mobile Service</h2>
      <p data-id="element-955" data-mp="29kseh9p626j4">
        Replacing headlight assemblies can cost hundreds or even thousands of
        dollars. Our restoration service is a fraction of the cost and yields
        incredible results. If you are searching for "Mobile Detailing Ventura
        County" or "Car Detailing Camarillo" to fix your cloudy headlights,
        Mike's Pro Detail will come directly to your home or workplace to
        perform the restoration.
      </p>

      <h2 data-id="element-956" data-mp="2xbws4o7q3hdp">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-957" data-mp="2xav31ftvgucz">
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-958" data-mp="32bj5qyrgqzn1">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-959" data-mp="3hfc7a9k2vhru">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/engine-bay-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-960" data-mp="33f1e2zvapr3i">
          Engine Bay Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-961" data-mp="3v7s3aqekafj9">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}